<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells>
        <app-input
          @input.native="onGroupsSearch"
          v-model="search"
          class="input--200"
          type="search"
          placeholder="Search by group name"
          name="search"
        />
      </app-cells>
      <div v-for="climbing_table_item of climbing_table" :key="climbing_table_item.name" class="climbing-block">
        <app-cells v-if="climbing_table_item.name">
          <h2 class="title">{{ climbing_table_item.name }}</h2>
          <div v-if="climbing_table_item.today" class="status-title" :style="{ 'backgroundColor': '#77b017' }">Today</div>
        </app-cells>
        <v-client-table
          :data="climbing_table_item.items"
          :columns="table.columns"
          :options="table.options"
          class="table-default"
          :class="{ 'table-default--loading': table_preloader }"
          @row-click="openGroupPreview"
        >
          <template #h__route.name><div class="table-default__left">Route</div></template>
          <template #h__current_camp><div class="table-default__left">Camp</div></template>
          <template #h__chief_guide><div class="table-default__left">Guide</div></template>
          <template #h__name><div class="table-default__left">Name</div></template>
          <template #h__type><div class="table-default__left">Type</div></template>
          <template #h__toilet>Portable<br>toilets</template>
          <template #h__showers>Portable<br>showers</template>
          <template #h__porters>Personal<br>porters</template>
          <template #h__phone>Satellite<br>phone</template>
          <template #name="{ row }">
            <div class="table-default__left table-default__controls">
              <a
                :href="`${row.gps?.shared_link}`"
                target="_blank"
                class="table-default__activity table-default__activity--gps"
                :class="{'table-default__activity--active': row.gps}"
              />
              <router-link
                :to="{ name: 'climbing-groups-detail-clients', params: { id: row.id } }"
                class="table-default__link-border table-default__link-border--inline"
              >
                {{ row.name }}
              </router-link> ({{ row.pax }})
            </div>
          </template>
          <template #chief_guide="{ row }">
            <div class="table-default__left">
              {{ row.chief_guide ? row.chief_guide.full_name : '-' }}
            </div>
          </template>
          <template #tour_start="{ row }">
            {{ dateFormat(row.tour_start) }}
          </template>
          <template #resupply="{ row }">
            {{ dateFormat(row.resupply) }}
          </template>
          <template #summit="{ row }">
            {{ dateFormat(row.summit) }}
          </template>
          <template #tour_end="{ row }">
            {{ dateFormat(row.tour_end) }}
          </template>
          <template #type="{ row }">
            <div class="table-default__left">
              <span class="status-tag" :style="{ 'backgroundColor': row.type.color || '#8a8a8a' }">
                {{ row.type ? row.type.name : '-' }}
              </span>
            </div>
          </template>
          <template #route.name="{ row }">
            <div class="table-default__left">{{ row.route.name }} {{ row.route.duration }}</div>
          </template>
          <template #current_camp="{ row }">
            <div class="table-default__left">{{ row.current_camp?.name }}</div>
          </template>
          <template #toilet="{ row }">
            <span v-if="row.additional_services.toilet" class="status-tag" :style="{ backgroundColor: '#424242' }">
              {{ row.additional_services.toilet }}
            </span>
            <span v-else>-</span>
          </template>
          <template #showers="{ row }">
            <span v-if="row.additional_services.shower" class="status-tag" :style="{ backgroundColor: '#424242' }">
              {{ row.additional_services.shower }}
            </span>
            <span v-else>-</span>
          </template>
          <template #porters="{ row }">
            <span v-if="row.additional_services.porter" class="status-tag" :style="{ backgroundColor: '#424242' }">
              {{ row.additional_services.porter }}
            </span>
            <span v-else>-</span>
          </template>
          <template #phone="{ row }">
            <span v-if="row.additional_services.phone" class="status-tag" :style="{ backgroundColor: '#424242' }">
              {{ row.additional_services.phone }}
            </span>
            <span v-else>-</span>
          </template>
        </v-client-table>
      </div>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table_length" :page="pagination.page" :limit="40" />
      </app-cells>
    </template>
  </div>
</template>

<script>
import { getClimbingGroupsByDates } from '@/http'
import debounce from 'lodash/debounce'
import { format, parse } from 'date-fns'
import previewRow from './previewRow'

export default {
  name: 'ClimbingGroupsInTourList',
  mixins: [previewRow],
  data() {
    return {
      is_loaded: false,
      search: null,
      climbing_table: [],
      table_length: 0,
      table: {
        items: [],
        columns: ['name', 'type', 'route.name', 'tour_start', 'resupply', 'summit', 'tour_end'],
        options: {
          headings: {
            'name': 'Name',
            'type': 'Type',
            'route.name': 'Route',
            'chief_guide': 'Guide',
            'current_camp': 'Camp',
            'toilet': 'Portable toilets',
            'showers': 'Portable showers',
            'porters': 'Personal porters',
            'phone': 'Satellite phone',
            'resupply': 'Resupply',
            'summit': 'Summit',
            'tour_start': 'Start',
            'tour_end': 'Finish',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 40,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader: false,
    }
  },
  computed: {
    show_tabs() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager') ||
        this.$store.state.userGroups.map(item => item.code).includes('climbing_supervisor') ||
        this.$store.state.userGroups.map(item => item.code).includes('climbing_manager') ||
        this.$store.state.userGroups.map(item => item.code).includes('climbing_assistant')
    },
    is_operations_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('operations_manager')
    },
  },
  created() {
    if (this.show_tabs) this.table.columns = ['name', 'type', 'route.name', 'tour_start', 'resupply', 'summit', 'tour_end', 'chief_guide', 'current_camp', 'toilet', 'showers', 'porters', 'phone']
    if (this.is_operations_manager) this.table.columns.splice(7, 0, 'chief_guide')
    this.fetchItems()
  },
  methods: {
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM') : '-'
    },
    fetchItems() {
      const params = {
        page: this.pagination.page,
        page_size: 40,
        search: this.search,
        category: 'in_tour',
        group_by: 'tour_end',
        range_type: 'day',
        ordering: 'tour_end,id'
      }
      getClimbingGroupsByDates(params)
        .then(res => {
          this.climbing_table = res.data.results.length ? res.data.results : [{name: '', items: []}]
          this.pagination.pages = res.data.pages
          this.pagination.count = res.data.count
          this.table_length = 0
          res.data.results.forEach(item => {
            this.table_length += item.items.length
          })
          this.is_loaded = true
          this.table_preloader = false
        })
    },
    onGroupsSearch() {
      this.table_preloader = true
      this.searchGroups(this)
    },
    searchGroups: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
  }
}
</script>

<style lang="sass" scoped>
.climbing-block
  margin-bottom: 30px
</style>
